import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Form,
  Modal,
  Spinner,
  Table,
} from 'react-bootstrap';
import { getFieldValueLabel } from './configurationAdminUtils';
import {
  analyzePartAutoBulkRows,
  createBlankPartAutoBulkRow,
  createPartAutoBulkRows,
  getNextPartAutoBulkRuleGroupId,
  getPartAutoBulkTemplateRows,
  getPartAutoValueOptionLabel,
  mergeImportedPartAutoBulkRows,
  parsePartAutoBulkImportMatrix,
} from './partAutoSelectBulkUtils';

const loadSpreadsheetLibrary = () => import('xlsx');

const safeFilenamePart = (value) => String(value || 'part-auto-pick')
  .trim()
  .replace(/[^a-z0-9]+/gi, '-')
  .replace(/^-|-$/g, '')
  .toLowerCase();

const buildWorkbook = (XLSX, {
  station,
  rules,
  assignedParts,
  assignedFieldValues,
  referenceFields,
  numericFields,
}) => {
  const workbook = XLSX.utils.book_new();
  const instructions = XLSX.utils.aoa_to_sheet([
    ['Part Auto Pick rules bulk upload'],
    ['Selected station', station.stationName],
    ['Station ID', station.id],
    [],
    ['Instructions'],
    ['Each spreadsheet row becomes one SP_PartMatchRules API row.'],
    ['Leave Id blank to create a row. Use an existing Id only to update that exact row.'],
    ['Rows with the same PartId, MasterStationId, and RuleGroupId are AND conditions and must use the same Quantity.'],
    ['Different RuleGroupId values for the same PartId and MasterStationId are OR groups.'],
    ['ConditionType accepts SelectedValue, Numeric, or Always.'],
    ['SelectedValue requires RequiredFieldValueId. Numeric requires NumericFieldId and optionally MinValue/MaxValue.'],
    ['Always leaves both match IDs and both range values blank and must be the only row in its group.'],
    ['Quantity defaults to 1. Quantity 0 is accepted and saved as null.'],
    ['Pump-derived quantity: add a Numeric row for the Pump Configuration Quantity field with blank MinValue and MaxValue. The Quantity column becomes the per-pump multiplier.'],
    ['The pump Quantity row can be the only row to include every pump, or it can be ANDed with Pump Description or other conditions to include only matching pump rows.'],
    ['Only parts assigned in Step 6, the selected MasterStationId, and values/fields assigned in Step 5 are valid. Use the lookup sheets for IDs.'],
    ['Omitted rows are unchanged; bulk mode never deletes rules.'],
    ['Part Auto Pick rules are scoped by PartId and MasterStationId. Updating one station type does not update another station type.'],
  ]);
  instructions['!cols'] = [{ wch: 28 }, { wch: 100 }];

  const rulesSheet = XLSX.utils.aoa_to_sheet(getPartAutoBulkTemplateRows(rules));
  rulesSheet['!cols'] = [
    { wch: 12 }, { wch: 12 }, { wch: 16 }, { wch: 18 }, { wch: 24 },
    { wch: 18 }, { wch: 14 }, { wch: 14 }, { wch: 12 },
  ];

  const partRows = [
    ['Category', 'Category Name', 'PartId', 'Part Number', 'Part Name'],
    ...assignedParts.map((part) => [
      part.categoryCode || '',
      part.categoryName || '',
      part.id,
      part.partNumber,
      part.name,
    ]),
  ];
  const partsSheet = XLSX.utils.aoa_to_sheet(partRows);
  partsSheet['!cols'] = [{ wch: 14 }, { wch: 30 }, { wch: 12 }, { wch: 20 }, { wch: 58 }];

  const fieldById = new Map(referenceFields.map((field) => [field.id, field]));
  const matchRows = [
    ['ConditionType', 'Field', 'ConfigFieldId', 'Value', 'RequiredFieldValueId', 'NumericFieldId'],
    ...assignedFieldValues.map((value) => {
      const field = fieldById.get(value.configFieldId);
      return ['SelectedValue', field?.fieldName || `Field ${value.configFieldId}`, value.configFieldId, getFieldValueLabel(value), value.id, ''];
    }),
    ...numericFields.map((field) => ['Numeric', field.fieldName, field.id, '', '', field.id]),
    ['Always', 'No condition', '', '', '', ''],
  ];
  const matchesSheet = XLSX.utils.aoa_to_sheet(matchRows);
  matchesSheet['!cols'] = [{ wch: 18 }, { wch: 34 }, { wch: 16 }, { wch: 48 }, { wch: 24 }, { wch: 18 }];

  XLSX.utils.book_append_sheet(workbook, instructions, 'Instructions');
  XLSX.utils.book_append_sheet(workbook, rulesSheet, 'Part Auto Pick Rules');
  XLSX.utils.book_append_sheet(workbook, partsSheet, 'Assigned Parts');
  XLSX.utils.book_append_sheet(workbook, matchesSheet, 'Match Lookups');
  return workbook;
};

const readWorkbookMatrix = async (file) => {
  const XLSX = await loadSpreadsheetLibrary();
  const workbook = XLSX.read(await file.arrayBuffer(), { type: 'array' });
  const sheet = workbook.Sheets['Part Auto Pick Rules'] || workbook.Sheets[workbook.SheetNames[0]];
  if (!sheet) throw new Error('The workbook does not contain a readable sheet.');
  return XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '', raw: false });
};

const PartAutoSelectBulkEditor = ({
  show,
  station,
  baselineRules,
  allRules,
  assignedParts,
  assignedFieldValues,
  referenceFields,
  numericFields,
  saving,
  validation,
  onClose,
  onSave,
}) => {
  const [rows, setRows] = useState([]);
  const [importError, setImportError] = useState('');
  const [importNotice, setImportNotice] = useState('');
  const [pasteText, setPasteText] = useState('');
  const [showPaste, setShowPaste] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!show || !station) return;
    setRows(createPartAutoBulkRows(baselineRules));
    setImportError('');
    setImportNotice('');
    setPasteText('');
    setShowPaste(false);
  }, [baselineRules, show, station]);

  const analysis = useMemo(() => station ? analyzePartAutoBulkRows({
    rows,
    baselineRules,
    allRules,
    assignedParts,
    assignedFieldValues,
    numericFields,
    stationId: station.id,
  }) : null, [allRules, assignedFieldValues, assignedParts, baselineRules, numericFields, rows, station]);

  const selectedValueGroups = useMemo(() => referenceFields
    .map((field) => ({
      field,
      values: assignedFieldValues
        .filter((value) => Number(value.configFieldId) === Number(field.id))
        .sort((left, right) => getFieldValueLabel(left).localeCompare(getFieldValueLabel(right), undefined, { numeric: true })),
    }))
    .filter((group) => group.values.length > 0)
    .sort((left, right) => left.field.fieldName.localeCompare(right.field.fieldName)), [assignedFieldValues, referenceFields]);

  const sortedRows = useMemo(() => [...rows].sort((left, right) => (
    Number(left.stationId) - Number(right.stationId)
    || Number(left.partId) - Number(right.partId)
    || Number(left.ruleGroupId) - Number(right.ruleGroupId)
    || Number(left.id || Number.MAX_SAFE_INTEGER) - Number(right.id || Number.MAX_SAFE_INTEGER)
    || left.clientKey.localeCompare(right.clientKey)
  )), [rows]);

  if (!station || !analysis) return null;

  const updateRow = (clientKey, changes) => {
    setRows((current) => {
      const source = current.find((row) => row.clientKey === clientKey);
      if (!source) return current;
      const propagateGroup = Object.prototype.hasOwnProperty.call(changes, 'partId')
        || Object.prototype.hasOwnProperty.call(changes, 'stationId')
        || Object.prototype.hasOwnProperty.call(changes, 'ruleGroupId')
        || Object.prototype.hasOwnProperty.call(changes, 'quantity');
      return current.map((row) => {
        const isSameGroup = Number(row.stationId) === Number(source.stationId)
          && Number(row.partId) === Number(source.partId)
          && Number(row.ruleGroupId) === Number(source.ruleGroupId);
        if (row.clientKey !== clientKey && (!propagateGroup || !isSameGroup)) return row;
        const next = { ...row, ...changes };
        if (next.conditionType === 'value') {
          next.fieldId = '';
          next.minValue = '';
          next.maxValue = '';
        } else if (next.conditionType === 'numeric') {
          next.requiredValueId = '';
        } else if (next.conditionType === 'default') {
          next.requiredValueId = '';
          next.fieldId = '';
          next.minValue = '';
          next.maxValue = '';
        }
        return next;
      });
    });
  };

  const addRuleGroup = () => {
    const initialType = assignedFieldValues.length ? 'value' : numericFields.length ? 'numeric' : 'default';
    setRows((current) => [...current, createBlankPartAutoBulkRow({
      partId: assignedParts[0]?.id || '',
      stationId: station.id,
      ruleGroupId: getNextPartAutoBulkRuleGroupId(allRules, current),
      conditionType: initialType,
      requiredValueId: initialType === 'value' ? assignedFieldValues[0]?.id || '' : '',
      fieldId: initialType === 'numeric' ? numericFields[0]?.id || '' : '',
      quantity: 1,
    })]);
  };

  const latestGroupRows = () => {
    const last = sortedRows[sortedRows.length - 1];
    return last ? rows.filter((row) => (
      Number(row.partId) === Number(last.partId)
      && Number(row.ruleGroupId) === Number(last.ruleGroupId)
    )) : [];
  };

  const addAndCondition = () => {
    setRows((current) => {
      const ordered = [...current].sort((left, right) => (
        Number(left.stationId) - Number(right.stationId)
        || Number(left.partId) - Number(right.partId)
        || Number(left.ruleGroupId) - Number(right.ruleGroupId)
      ));
      const source = ordered[ordered.length - 1];
      if (!source) return current;
      const sourceGroup = current.filter((row) => (
        Number(row.stationId) === Number(source.stationId)
        && Number(row.partId) === Number(source.partId)
        && Number(row.ruleGroupId) === Number(source.ruleGroupId)
      ));
      const usedValueIds = new Set(sourceGroup.map((row) => Number(row.requiredValueId)).filter(Boolean));
      const usedNumericFieldIds = new Set(sourceGroup.map((row) => Number(row.fieldId)).filter(Boolean));
      const nextValue = assignedFieldValues.find((value) => !usedValueIds.has(Number(value.id))) || assignedFieldValues[0];
      const nextNumericField = numericFields.find((field) => !usedNumericFieldIds.has(Number(field.id))) || numericFields[0];
      const type = assignedFieldValues.length ? 'value' : numericFields.length ? 'numeric' : 'default';
      return [...current, createBlankPartAutoBulkRow({
        partId: source.partId,
        stationId: station.id,
        ruleGroupId: source.ruleGroupId,
        conditionType: type,
        requiredValueId: type === 'value' ? nextValue?.id || '' : '',
        fieldId: type === 'numeric' ? nextNumericField?.id || '' : '',
        quantity: source.quantity,
      })];
    });
  };

  const importMatrix = (matrix, sourceLabel) => {
    const imported = parsePartAutoBulkImportMatrix(matrix);
    const nextRows = mergeImportedPartAutoBulkRows(rows, imported);
    setRows(nextRows);
    setImportError('');
    setImportNotice(`${imported.length} row${imported.length === 1 ? '' : 's'} imported from ${sourceLabel}. Review validation and changes before saving.`);
  };

  const handleFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    try {
      importMatrix(await readWorkbookMatrix(file), file.name);
    } catch (error) {
      setImportNotice('');
      setImportError(error.message || 'Unable to read the selected file.');
    }
  };

  const handlePasteImport = async () => {
    try {
      const XLSX = await loadSpreadsheetLibrary();
      const workbook = XLSX.read(pasteText, { type: 'string' });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      importMatrix(XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '', raw: false }), 'the pasted table');
      setPasteText('');
    } catch (error) {
      setImportNotice('');
      setImportError(error.message || 'Unable to read the pasted table.');
    }
  };

  const downloadSpreadsheet = async (includeCurrentRules) => {
    try {
      const XLSX = await loadSpreadsheetLibrary();
      const workbook = buildWorkbook(XLSX, {
        station,
        rules: includeCurrentRules ? baselineRules : [],
        assignedParts,
        assignedFieldValues,
        referenceFields,
        numericFields,
      });
      const suffix = includeCurrentRules ? 'current-part-auto-pick-rules' : 'part-auto-pick-upload-template';
      XLSX.writeFile(workbook, `${safeFilenamePart(station.stationName)}-${suffix}.xlsx`);
    } catch (error) {
      setImportNotice('');
      setImportError(error.message || 'Unable to create the spreadsheet.');
    }
  };

  const hasErrors = analysis.errorCount > 0;
  const latestRows = latestGroupRows();
  const latestGroupIsAlways = latestRows.some((row) => row.conditionType === 'default');
  const displayedRuleGroupIds = [...new Set(rows
    .map((row) => Number(row.ruleGroupId))
    .filter((ruleGroupId) => Number.isInteger(ruleGroupId) && ruleGroupId > 0))]
    .sort((left, right) => left - right);
  const displayedRuleGroupIdText = displayedRuleGroupIds.length
    ? displayedRuleGroupIds.join(', ')
    : 'None';
  let previousPartId = null;
  let previousGroupId = null;

  return (
    <Modal show={show} onHide={saving ? undefined : onClose} size="xl" centered dialogClassName="config-admin-bulk-modal config-admin-rule-bulk-modal config-admin-part-auto-bulk-modal">
      <Modal.Header closeButton={!saving}><Modal.Title>Bulk edit and upload Part Auto Pick rules</Modal.Title></Modal.Header>
      <Modal.Body>
        <div className="config-admin-bulk-scope config-admin-rule-bulk-scope">
          <div><small>Station</small><strong>{station.stationName}</strong><span>ID {station.id}</span></div>
          <div><small>Eligible parts</small><strong>{assignedParts.length}</strong><span>{analysis.configuredPartCount} represented in the editor</span></div>
          <div>
            <small>Rule group IDs</small>
            <strong title={displayedRuleGroupIdText}>{displayedRuleGroupIdText}</strong>
            <span>{analysis.groupCount} OR {analysis.groupCount === 1 ? 'group' : 'groups'} &bull; {rows.length} API condition rows</span>
          </div>
        </div>

        <Alert variant="warning" className="small">
          Part Auto Pick rules are saved for the selected <strong>PartId</strong> and <strong>MasterStationId</strong>. Only assigned parts, the current MasterStationId, and station-available match values are accepted here.
        </Alert>
        <Alert variant="info" className="small">
          Rows with the same Part, Station, and Rule Group are <strong>AND</strong>. Different groups for that same part and station are <strong>OR</strong>. An Always group is unconditional and contains one row. Bulk mode creates and updates only; omitted rows are never deleted.
          {' '}An unbounded Numeric condition on the Pump Configuration <strong>Quantity</strong> field makes the group quantity pump-derived; its Quantity value is the per-pump multiplier.
        </Alert>
        {validation && <Alert variant="danger">{validation}</Alert>}
        {importError && <Alert variant="danger" dismissible onClose={() => setImportError('')}>{importError}</Alert>}
        {importNotice && <Alert variant="success" dismissible onClose={() => setImportNotice('')}>{importNotice}</Alert>}

        <div className="config-admin-bulk-toolbar">
          <div className="d-flex flex-wrap gap-2">
            <Button type="button" variant="outline-primary" onClick={() => downloadSpreadsheet(false)}><i className="bi bi-file-earmark-spreadsheet me-2" aria-hidden="true"></i>Download upload template</Button>
            <Button type="button" variant="outline-primary" onClick={() => downloadSpreadsheet(true)} disabled={baselineRules.length === 0}><i className="bi bi-download me-2" aria-hidden="true"></i>Export current rules</Button>
            <Button type="button" variant="outline-primary" onClick={() => fileInputRef.current?.click()}><i className="bi bi-upload me-2" aria-hidden="true"></i>Upload spreadsheet</Button>
            <input ref={fileInputRef} type="file" accept=".xlsx,.xls,.csv,.tsv" className="d-none" onChange={handleFile} />
            <Button type="button" variant="outline-primary" onClick={() => setShowPaste((current) => !current)}><i className="bi bi-clipboard me-2" aria-hidden="true"></i>Paste table</Button>
          </div>
          <div className="d-flex flex-wrap gap-2">
            <Button type="button" variant="outline-primary" onClick={addAndCondition} disabled={rows.length === 0 || latestGroupIsAlways || (!assignedFieldValues.length && !numericFields.length)} title={latestGroupIsAlways ? 'An Always group cannot contain AND conditions.' : undefined}><i className="bi bi-plus-circle me-2" aria-hidden="true"></i>Add AND condition</Button>
            <Button type="button" onClick={addRuleGroup} disabled={assignedParts.length === 0}><i className="bi bi-plus-circle me-2" aria-hidden="true"></i>Add OR rule group</Button>
          </div>
        </div>

        {showPaste && (
          <div className="config-admin-bulk-paste">
            <Form.Group>
              <Form.Label>Paste Part Auto Pick rows including the header row</Form.Label>
              <Form.Control as="textarea" rows={5} value={pasteText} onChange={(event) => setPasteText(event.target.value)} placeholder="Id&#9;PartId&#9;MasterStationId&#9;RuleGroupId&#9;ConditionType&#9;RequiredFieldValueId&#9;NumericFieldId&#9;MinValue&#9;MaxValue&#9;Quantity" />
              <Form.Text>ConditionType accepts SelectedValue, Numeric, or Always. MasterStationId must match the selected station. Matching existing IDs update staged rows; blank IDs add API rows.</Form.Text>
            </Form.Group>
            <div className="mt-2"><Button type="button" size="sm" onClick={handlePasteImport} disabled={!pasteText.trim()}>Add pasted rows</Button></div>
          </div>
        )}

        <div className="table-responsive config-admin-bulk-table-wrap">
          <Table bordered hover className="config-admin-bulk-table config-admin-part-auto-bulk-table mb-0">
            <thead><tr><th>ID</th><th>Part</th><th>Station</th><th>Rule logic</th><th>Condition type</th><th>Match condition</th><th>Minimum</th><th>Maximum</th><th>Quantity</th><th>Status</th><th aria-label="Row actions"></th></tr></thead>
            <tbody>
              {sortedRows.map((row) => {
                const rowErrors = analysis.errorsByKey[row.clientKey] || [];
                const operation = analysis.operations.find((candidate) => candidate.clientKey === row.clientKey);
                const status = rowErrors.length > 0 ? 'Error' : operation?.method === 'POST' ? 'New' : operation?.method === 'PUT' ? 'Changed' : 'Unchanged';
                const isNewPart = Number(row.partId) !== Number(previousPartId);
                const isFirstInGroup = isNewPart || Number(row.ruleGroupId) !== Number(previousGroupId);
                const showOr = previousPartId !== null && !isNewPart && isFirstInGroup;
                previousPartId = row.partId;
                previousGroupId = row.ruleGroupId;
                return (
                  <React.Fragment key={row.clientKey}>
                    {showOr && <tr className="config-admin-part-auto-or-row"><td colSpan={11}><span>OR</span></td></tr>}
                    <tr className={rowErrors.length > 0 ? 'table-danger' : ''}>
                      <td><Form.Control value={row.id} disabled aria-label="Part match rule ID" placeholder="New" /></td>
                      <td>
                        <Form.Select value={row.partId} onChange={(event) => updateRow(row.clientKey, { partId: event.target.value })} aria-label="Assigned part">
                          <option value="">Choose part</option>
                          {assignedParts.map((part) => <option key={part.id} value={part.id}>{part.partNumber || `Part ${part.id}`} — {part.name} (ID {part.id})</option>)}
                        </Form.Select>
                      </td>
                      <td><Form.Control value={row.stationId || station.id} disabled aria-label="Master station ID" /></td>
                      <td>
                        <div className="config-admin-part-auto-logic-cell">
                          <Badge bg={isFirstInGroup ? 'primary' : 'light'} text={isFirstInGroup ? undefined : 'dark'}>{isFirstInGroup ? `Group ${row.ruleGroupId || '?'}` : 'AND'}</Badge>
                          <Form.Control type="number" min="1" step="1" value={row.ruleGroupId} onChange={(event) => updateRow(row.clientKey, { ruleGroupId: event.target.value })} aria-label="Rule group ID" />
                        </div>
                      </td>
                      <td>
                        <Form.Select value={row.conditionType} onChange={(event) => updateRow(row.clientKey, { conditionType: event.target.value })} aria-label="Condition type">
                          <option value="value">Selected value</option>
                          <option value="numeric">Numeric range</option>
                          <option value="default">Always selected</option>
                        </Form.Select>
                      </td>
                      <td>
                        {row.conditionType === 'value' ? (
                          <Form.Select value={row.requiredValueId} onChange={(event) => updateRow(row.clientKey, { requiredValueId: event.target.value })} aria-label="Required field value">
                            <option value="">Choose available value</option>
                            {selectedValueGroups.map((group) => (
                              <optgroup key={group.field.id} label={`${group.field.fieldName} (Field ${group.field.id})`}>
                                {group.values.map((value) => <option key={value.id} value={value.id}>{getPartAutoValueOptionLabel(value, group.field)} (ID {value.id})</option>)}
                              </optgroup>
                            ))}
                          </Form.Select>
                        ) : row.conditionType === 'numeric' ? (
                          <Form.Select value={row.fieldId} onChange={(event) => updateRow(row.clientKey, { fieldId: event.target.value })} aria-label="Numeric field">
                            <option value="">Choose numeric field</option>
                            {numericFields.map((field) => <option key={field.id} value={field.id}>{field.fieldName} (ID {field.id})</option>)}
                          </Form.Select>
                        ) : <span className="config-admin-part-auto-always"><i className="bi bi-check-circle-fill" aria-hidden="true"></i>Always matches</span>}
                      </td>
                      <td><Form.Control type="number" step="0.01" placeholder="No minimum" value={row.minValue} disabled={row.conditionType !== 'numeric'} onChange={(event) => updateRow(row.clientKey, { minValue: event.target.value })} aria-label="Minimum match" /></td>
                      <td><Form.Control type="number" step="0.01" placeholder="No maximum" value={row.maxValue} disabled={row.conditionType !== 'numeric'} onChange={(event) => updateRow(row.clientKey, { maxValue: event.target.value })} aria-label="Maximum match" /></td>
                      <td><Form.Control type="number" min="0" step="1" value={row.quantity} onChange={(event) => updateRow(row.clientKey, { quantity: event.target.value })} aria-label="Rule group quantity" /></td>
                      <td><Badge bg={status === 'Error' ? 'danger' : status === 'New' ? 'success' : status === 'Changed' ? 'warning' : 'light'} text={status === 'Unchanged' || status === 'Changed' ? 'dark' : undefined}>{status}</Badge></td>
                      <td>{!row.isExisting && <Button type="button" size="sm" variant="outline-danger" onClick={() => setRows((current) => current.filter((candidate) => candidate.clientKey !== row.clientKey))} aria-label="Remove new Part Auto Pick rule row"><i className="bi bi-trash" aria-hidden="true"></i></Button>}</td>
                    </tr>
                    {rowErrors.length > 0 && <tr className="config-admin-bulk-error-row"><td colSpan={11}>{rowErrors.join(' ')}</td></tr>}
                  </React.Fragment>
                );
              })}
              {rows.length === 0 && <tr><td colSpan={11} className="text-center text-muted py-4">No Part Auto Pick rules exist for assigned parts. Add an OR rule group or upload a spreadsheet.</td></tr>}
            </tbody>
          </Table>
        </div>
      </Modal.Body>
      <Modal.Footer className="config-admin-bulk-footer">
        <div className="config-admin-bulk-summary" aria-live="polite">
          <Badge bg="success">{analysis.createCount} to create</Badge>
          <Badge bg="warning" text="dark">{analysis.updateCount} to update</Badge>
          <Badge bg="light" text="dark">{analysis.unchangedCount} unchanged</Badge>
          {hasErrors && <Badge bg="danger">{analysis.errorCount} validation {analysis.errorCount === 1 ? 'error' : 'errors'}</Badge>}
        </div>
        <div className="d-flex gap-2">
          <Button variant="outline-secondary" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={() => onSave({ rows, operations: analysis.operations, baselineRules })} disabled={saving || hasErrors || analysis.operations.length === 0}>
            {saving && <Spinner animation="border" size="sm" className="me-2" />}
            Save {analysis.operations.length} change{analysis.operations.length === 1 ? '' : 's'}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default PartAutoSelectBulkEditor;
